import type { SlideProps } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { usePhase, useStore, useWorkshop } from "@/state/store";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { Timer } from "@/components/primitives/Timer";
import { QrCode, zugangsAdresse } from "./_zugang";

/**
 * Zugangsfolie. Steht, während die Gruppe sich anmeldet, und läuft während
 * der Arbeitsphase nebenher weiter.
 *
 * Anders als bei den Gremien gibt es keine gemeinsame PIN: Der Strategietag
 * nutzt Einmal-Codes (`codeLogin`), jede Person meldet sich mit dem eigenen
 * Code an — deshalb zeigt diese Folie nur Adresse und QR-Code, keinen
 * gemeinsamen Zugangswert, den man vorlesen könnte.
 *
 * Trägt auch den Timer und die Zahlen der Runde. Die Workshop-Oberfläche
 * übernimmt die Leinwand hier bewusst nicht (`workshopOverlayPhases:
 * ["reveal"]`) — Adresse und QR-Code müssen stehen bleiben, damit
 * Nachzügler aus dem Publikum noch hereinfinden.
 */
export function Slide03Zugang(_: SlideProps) {
  const { buehnenPlaetze } = useOffsiteConfig();
  const phase = usePhase();
  const { state } = useStore();
  const { timerEndsAt } = useWorkshop().phase;
  const timerRunning = typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const zahlen = useSitzungsZahlen(
    phase === "waiting" || phase === "working",
    state.auth.token,
  );
  // Während die KI baut, bleibt diese Folie stehen (overnightAnnouncementSlide)
  // und der Moderator zeigt daneben SVO OS. Die Wartezeit soll sichtbar
  // vorangehen, statt als stummes Bild zu stehen.
  const bau = useOvernightStatus(phase === "overnight");

  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />

      {/* Zwei Schritte des Ablaufs zeigen diese Folie: „Warten auf die Gruppe"
       *  und „Prompten öffnen". Adresse und QR bleiben beide Male stehen,
       *  damit Nachzügler hereinkommen — aber die Überschrift sagt, was
       *  gerade dran ist. */}
      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Jetzt ihr
        </div>
        <h1 className="deck-title rise rise-2" data-testid="zugang-titel">
          {phase === "working"
            ? "Die Fragen sind offen"
            : "Scannt den Code — und ihr seid drin"}
        </h1>
      </header>

      <div className="ar-work">
        <div className="rise rise-3">
          <div className="ar-url">{zugangsAdresse}</div>
          <p className="ar-lede" style={{ marginTop: "var(--s-6)" }}>
            QR scannen und ihr seid angemeldet — die 6 auf der Bühne scannen
            stattdessen ihre Karte.
          </p>
          <p className="ar-lede">
            {phase === "working"
              ? "Drei Fragen, eigene Worte. Nehmt euch Zeit — die Uhr läuft noch nicht."
              : "Drei Fragen, eigene Worte. Es gibt keine falsche Antwort."}
          </p>

          {timerRunning && typeof timerEndsAt === "number" && (
            <div className="ar-slide-timer" data-testid="slide-timer">
              <Timer deadline={timerEndsAt} size="side" />
            </div>
          )}

          {phase === "overnight" && (
            <p className="ar-slide-bau" data-testid="slide-bau">
              {bau
                ? bau.status === "complete" ||
                  (bau.total > 0 && bau.done >= bau.total)
                  ? `Fertig — ${bau.done} Prototypen`
                  : `${bau.done} von ${bau.total || "…"} Prototypen fertig`
                : "Die KI baut …"}
            </p>
          )}

          {/* Zahlen der Runde. Beim Warten beruhigt die Zahl den Moderator —
           *  er sieht ohne Nachfragen, ob schon Leute angemeldet sind. In der
           *  Arbeitsphase zeigt sie, wie weit die Gruppe ist. */}
          {zahlen && (phase === "waiting" || phase === "working") && (
            <p className="ar-slide-zahlen" data-testid="slide-zahlen">
              {/* Nach Rollen getrennt: Der Moderator sieht auf einen Blick,
                  ob die Bühne vollzählig ist — die Gesamtzahl sagte das
                  nicht. */}
              Bühne <strong>{zahlen.buehne}</strong>/{buehnenPlaetze ?? 6}
              {" · "}
              <strong>{zahlen.publikum}</strong> aus dem Publikum
              {phase === "working" && (
                <>
                  {" · "}
                  <strong>{zahlen.eingereicht}</strong> eingereicht
                </>
              )}
            </p>
          )}
        </div>

        <div className="rise rise-4">
          <QrCode />
          <div className="ar-qr-caption">Mit der Handykamera scannen</div>
        </div>
      </div>
    </div>
  );
}
